import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { omit, throttle } from 'lodash-es';
import lookie from 'lookie';
import { Divider } from 'antd';
import { AIConsultationFrom, TrackerCustomEvent, useTracker } from 'common-package';
import { Icon } from '@fuxi/eevee-icon';
import { useDispatch, useSelector } from 'react-redux';
import { OrientationType, ScreenOrientation } from '@capawesome/capacitor-screen-orientation';
import { selectDevicePlatform } from '@/store/device';
import { setCurrentCustomer } from '@/store/customer';
import { ALL_PAGES, SELECTED_CUSTOMER_ID } from '@/constant';
import { useNav } from '@/hooks';
import { THERAPY_TYPE_2_TEXT } from '@/pages/ai-consultation/utils';
import ExpandContent from './ExpandContent';
import cx from './index.module.scss';

interface Props {
  currentCustomerInfo: GetCustomerProfileResponse;
  isVerticalScreen: boolean;
  isTipOpen: boolean;
  loading: boolean;
  setIsTipOpen: Dispatch<SetStateAction<boolean>>;
  expand: boolean;
  setExpand: Dispatch<SetStateAction<boolean>>;
}

const MemberPhysiotherapyInfo: React.FC<Props> = ({
  currentCustomerInfo,
  isVerticalScreen,
  isTipOpen,
  setIsTipOpen,
  loading = true,
  expand,
  setExpand,
}) => {
  const { sendEvent } = useTracker();
  const nav = useNav();
  const dispatch = useDispatch();
  const devicePlatform = useSelector(selectDevicePlatform);

  const isHasMorePlan = useMemo(() => {
    return currentCustomerInfo?.recommendGoods?.length > 0 && expand;
  }, [currentCustomerInfo?.recommendGoods?.length, expand]);

  const planTitleName = useMemo(() => {
    const selectTherapyTypes = currentCustomerInfo?.hopeTypes;
    if (!selectTherapyTypes?.length) return '';

    if (selectTherapyTypes?.length > 1) {
      return '身体综合改善计划';
    } else {
      return THERAPY_TYPE_2_TEXT[selectTherapyTypes?.[0]] + '计划';
    }
  }, [currentCustomerInfo?.hopeTypes]);

  const handleExpand = useMemo(
    () =>
      throttle(() => {
        setExpand(preVal => !preVal);
      }, 200),
    [setExpand]
  );

  const handleNavAiConsultation = useCallback(() => {
    dispatch(
      setCurrentCustomer(
        omit({ ...currentCustomerInfo, isVerticalScreen, isTipOpen, setIsTipOpen }, ['isTipOpen', 'setIsTipOpen'])
      )
    );
    lookie.set(SELECTED_CUSTOMER_ID, currentCustomerInfo?.customerId);

    sendEvent?.(TrackerCustomEvent.AiConsultationClk, {
      et: 'CLK',
      c1: AIConsultationFrom.MemberDetail,
    });

    if (devicePlatform === 'android') {
      ScreenOrientation.lock({ type: OrientationType.PORTRAIT_PRIMARY });
      setIsTipOpen(true);
    } else {
      nav(ALL_PAGES.AI_CONSULTATION, {
        state: {
          action: 'member-detail',
        },
      });
    }
  }, [currentCustomerInfo, devicePlatform, dispatch, isTipOpen, isVerticalScreen, nav, sendEvent, setIsTipOpen]);

  useEffect(() => {
    if (isTipOpen && isVerticalScreen && devicePlatform === 'android') {
      nav(ALL_PAGES.AI_CONSULTATION, {
        state: {
          action: 'member-detail',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerticalScreen, isTipOpen, devicePlatform]);

  useEffect(() => {
    expand &&
      sendEvent(TrackerCustomEvent.ViewMemberTherapyAppealClk, {
        et: 'CLK',
      });
  }, [expand, sendEvent]);

  return (
    <div className={cx('member-physiotherapy-wrapper')}>
      {loading ? (
        <div className={cx('content')}>
          <div className={cx('header-wrapper-bg')} />
          <div className={cx('header-wrapper', 'loading-data')} />
          <Divider className={cx('divider')} />
          <div className={cx('add-service', { 'more-plan': isHasMorePlan }, { 'no-expand': !expand })}>
            <div className={cx('add-service-text')}>
              <Icon name="add-service" className={cx('icon')} />
              <span>新建服务，开始理疗</span>
            </div>
          </div>
        </div>
      ) : !!currentCustomerInfo?.hopeTypes?.length ? (
        <div className={cx('content')}>
          <div className={cx('header-wrapper-bg')} />
          <div className={cx('header-wrapper', { 'more-plan': isHasMorePlan })}>
            <div className={cx('info-wrapper')}>
              <div className={cx('user-info')}>
                <div className={cx('plan-name')}>{planTitleName}</div>
                {expand && (
                  <div className={cx('right-text')} onClick={() => nav(ALL_PAGES.BODY_CHANGE_3D)}>
                    <span>查看3D身体变化</span>
                    <Icon name="arrow-right" className={cx('icon')} />
                  </div>
                )}
              </div>
              <div
                className={cx('show-content', {
                  show: !!currentCustomerInfo?.hopeTypes?.length,
                })}
                onClick={handleExpand}
              >
                <span className={cx('text')}>{expand ? '收起' : '展开'}</span>
                <Icon name={expand ? 'arrow-up' : 'arrow-down'} className={cx('icon')} />
              </div>
            </div>
            <ExpandContent
              type="has-hopeType"
              expand={expand}
              customerId={currentCustomerInfo?.customerId}
              isHasMorePlan={isHasMorePlan}
            />
          </div>
          <Divider className={cx('divider', { hide: expand })} />

          <div
            className={cx('add-service', { 'more-plan': isHasMorePlan }, { expand }, { 'no-expand': !expand })}
            onClick={() => nav(ALL_PAGES.ADD_SERVICE)}
          >
            <div className={cx('add-service-text')}>
              <Icon name="add-service" className={cx('icon')} />
              <span>新建服务，开始理疗</span>
            </div>
          </div>
        </div>
      ) : (
        // 没有AI检测时 上下布局
        <div className={cx('content')}>
          <div className={cx('header-wrapper-bg')} />
          <div className={cx('header-wrapper')}>
            <div className={cx('info-wrapper')}>
              <div className={cx('user-info')}>
                <div className={cx('plan-name')}>理疗档案</div>

                <div className={cx('right-text')} onClick={handleNavAiConsultation}>
                  <span>体验AI问询，进行更完善的理疗推荐吧</span>
                  <Icon name="arrow-right" className={cx('icon')} />
                </div>
              </div>
              <div
                className={cx('show-content', {
                  show:
                    !!currentCustomerInfo?.physicalAssessment?.length ||
                    !!currentCustomerInfo?.adjustmentPurpose?.length,
                })}
                onClick={() => setExpand(preVal => !preVal)}
              >
                <span className={cx('text')}>{expand ? '收起' : '展开'}</span>
                <Icon name={expand ? 'arrow-up' : 'arrow-down'} className={cx('icon')} />
              </div>
            </div>
            <ExpandContent type="no-hopeType" expand={expand} customerId={currentCustomerInfo?.customerId} />
          </div>
          <Divider className={cx('divider', { hide: expand })} />

          <div
            className={cx('add-service', { 'expand-wrapper': expand }, { 'no-expand': !expand })}
            onClick={() => nav(ALL_PAGES.ADD_SERVICE)}
          >
            <div className={cx('add-service-text')}>
              <Icon name="add-service" className={cx('icon')} />
              <span>新建服务，开始理疗</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberPhysiotherapyInfo;
